import { WorkareaPricingData } from "../../domain/workareas/workareaPricing"
import { arrayMove } from '@dnd-kit/sortable'
import * as WorkareaItem from '../../domain/workareaItem'
import { ActualItemOrigin, ItemClass, ItemClasses, PricingType, PricingTypes, ProjectStatus, WorkareaItemType, WorkareaItemTypes } from '../../common/constants'
import { CatalogItem, defaultCatalogItem } from '../../domain/catalogItem'
import { RateCalculationData } from './RateCalculator'
import { CatalogTemplateWithItems, TemplateItem } from '../../domain/catalogTemplate'
import { YearlyBudget } from '../../domain/budget'
import groupBy from 'lodash/groupBy'
import { hasValue } from '../../helpers/nullable'
import { calculateUsageQuantity, distinctRateUnits, DistinctRateUnit, EnteredRateQuantities, normalizeRateUnits, QuantityChange, rateEntryKey, TemplateGroup } from '../../domain/productionRate'
import { isRecurringMatch } from './recurringWorkareaModel'
import { GetJobWorkareaItemsResponse, UpdateJobItemsRequest } from './itemsApi'

type WorkareaItemBase = WorkareaItem.WorkareaItemBase

export type ItemVisitData = {
  id: string
  billed: boolean
  owner: string
  date: string
  technicians: string
  taskIds: number[]
}

export const defaultItemVisitData = (): ItemVisitData => ({
  id: '',
  billed: false,
  owner: '',
  date: '',
  technicians: '',
  taskIds: []
})

export type ItemWorkareaData = {
  id: number
  name: string
  pricingType: PricingType
  isApproved: boolean
  isOnHold: boolean
  master?: boolean
  masterWorkareaId?: number | null
  costCodeId: number
  divisionId: number
  scheduleDateUtc?: string
  quotedPrice?: number
  recovery?: number
  pricing?: WorkareaPricingData | null
  workSubtotal?: number | null
  customQuotedPrice?: number
  notes?: string
  customerAndInvoiceNotes?: string
  invoiceDescription?: string | null
  sortIndex: number
  workareaGroupId?: string
  status?: ProjectStatus
  estimatedOccurrences?: number
}

export const defaultItemWorkareaData = (): ItemWorkareaData => ({
  id: 0,
  name: '',
  pricingType: PricingTypes.QuotedPrice,
  isOnHold: false,
  isApproved: true,
  costCodeId: 0,
  divisionId: 0,
  sortIndex: 0,
})

export type ViewItem = {
  id: number
  guid: string
  name: string
  defaultName: string
  hasCustomName: boolean
  canCustomizeName: boolean
  units: string
  cost: number
  totalCost: number
  costPlusTax: number
  taxPercentage: number
  applyTaxToBreakeven: boolean
  overheadPercentage: number
  hasCustomCost: boolean
  canCustomizeCost: boolean
  defaultCost: number
  breakeven: number
  profitMargin: number
  hasCustomProfitMargin: boolean
  canCustomizeProfitMargin: boolean
  defaultProfitMargin: number
  totalBreakeven: number
  unitPrice: number
  hasCustomUnitPrice: boolean
  originalCatalogCustomPrice?: number
  defaultUnitPrice: number
  totalPrice: number
  defaultTotalPrice: number
  canCustomizeTotalPrice: boolean
  hasCustomTotalPrice: boolean
  quantity: number
  totalEstimatedQuantity?: number
  isBillable: boolean
  isBillableLocked: boolean
  sortIndex: number
  isInvoiced: boolean
  isEntireRowDisabled: boolean
  rateCalculationData?: RateCalculationData
  isAdditional: boolean
  isAdditionalLocked: boolean
  isVisitReviewed: boolean
  isDeleted: boolean
  catalogItemId: number
  isEmptyRow?: boolean
  shouldShowPricingSync: boolean
  chargeTaxOnInvoice: boolean
  unsure: boolean
  workareaName: string
  catalogItemGuid: string
  workareaId: number
  isFromTimesheets: boolean
  visit?: ItemVisitData
  origin?: ActualItemOrigin
  originOwner?: string
  class: ItemClass
  differsFromMasterTask?: boolean
  dragId: string
  ifBillableTotalPrice?: number
  webEntryConsumptionDate?: string
}

export const defaultViewItem = (): ViewItem => {
  return {
    id: 0,
    guid: '',
    name: '',
    defaultName: '',
    hasCustomName: false,
    canCustomizeName: true,
    units: '',
    cost: 0,
    totalCost: 0,
    costPlusTax: 0,
    taxPercentage: 0,
    applyTaxToBreakeven: false,
    overheadPercentage: 0,
    hasCustomCost: false,
    canCustomizeCost: true,
    defaultCost: 0,
    breakeven: 0,
    profitMargin: 0,
    hasCustomProfitMargin: false,
    canCustomizeProfitMargin: true,
    defaultProfitMargin: 0,
    totalBreakeven: 0,
    unitPrice: 0,
    hasCustomUnitPrice: false,
    defaultUnitPrice: 0,
    totalPrice: 0,
    defaultTotalPrice: 0,
    canCustomizeTotalPrice: false,
    hasCustomTotalPrice: false,
    quantity: 0,
    isBillable: false,
    isBillableLocked: false,
    sortIndex: 0,
    isInvoiced: false,
    isEntireRowDisabled: false,
    isAdditional: false,
    isAdditionalLocked: false,
    isVisitReviewed: false,
    isDeleted: false,
    catalogItemId: 0,
    shouldShowPricingSync: false,
    chargeTaxOnInvoice: false,
    unsure: false,
    workareaName: '',
    catalogItemGuid: '',
    workareaId: 0,
    isFromTimesheets: false,
    class: ItemClasses.Materials,
    dragId: '',
    ifBillableTotalPrice: undefined,
  }
}

export const viewItemForDraggableCatalogItem = (catalogItem: CatalogItem, dragId: string): ViewItem => {
  return {
    ...defaultViewItem(),
    guid: catalogItem.guid,
    name: catalogItem.name,
    units: catalogItem.units ?? '',
    cost: catalogItem.adjustedCost,
    dragId
  }
}

export const viewItemForDraggableTemplate = (template: CatalogTemplateWithItems, dragId: string): ViewItem => {
  const catalogItem = (template.templateItems.length > 0
    ? template.templateItems[0].catalogItem
    : null)
    ?? defaultCatalogItem()

  return {
    ...defaultViewItem(),
    guid: template.id.toString(),
    name: template.name,
    units: catalogItem.units ?? '',
    cost: catalogItem.adjustedCost,
    dragId
  }
}

export type Action =
  | { type: 'NAME_CHANGED', guid: string, value: string, serverState: ServerState }
  | { type: 'IS_ADDITIONAL_CHANGED', guid: string, isVisitReviewed: boolean, value: boolean, serverState: ServerState, workareas: ItemWorkareaData[] }
  | { type: 'BILLABLE_CHANGED', guid: string, isVisitReviewed: boolean, value: boolean, serverState: ServerState, workareas: ItemWorkareaData[] }
  | { type: 'UNSURE_CHANGED', guid: string, isVisitReviewed: boolean, value: boolean, serverState: ServerState, workareas: ItemWorkareaData[] }
  | { type: 'TAXABLE_CHANGED', guid: string, isVisitReviewed: boolean, value: boolean, serverState: ServerState }
  | { type: 'QUANTITY_CHANGED', guid: string, value: number, serverState: ServerState }
  | { type: 'CUSTOM_COST_CHANGED', guid: string, value: number | null, serverState: ServerState }
  | { type: 'CUSTOM_PRICE_CHANGED', guid: string, value: number, serverState: ServerState }
  | { type: 'CUSTOM_TOTAL_PRICE_CHANGED', guid: string, value: number, serverState: ServerState }
  | { type: 'CUSTOM_MARGIN_CHANGED', guid: string, value: number, serverState: ServerState }
  | { type: 'UPDATE_SORTING', fromGuid: string, toGuid: string, serverState: ServerState }
  | { type: 'ADD_ITEM_FROM_CATALOG', catalogItem: CatalogItem, addItemParams: AddItemParams }
  | { type: 'ADD_TEMPLATE_FROM_CATALOG', template: CatalogTemplateWithItems, addItemParams: AddItemParams }
  | { type: 'DELETE_ITEM', guid: string, serverState: ServerState }
  | { type: 'UNDELETE_ITEM', guid: string, serverState: ServerState }
  | { type: 'RECEIPT_DELETED', receiptId: number, serverState: ServerState }
  | { type: 'RECEIPT_UNDELETED', receiptId: number, serverState: ServerState }
  | { type: 'SYNC_ITEM_PRICING', guid: string, budgets: YearlyBudget[], serverState: ServerState }
  | { type: 'SYNC_WORKAREA_PRICING', workareaId: number, budgets: YearlyBudget[], serverState: ServerState }
  | { type: 'CLEAR_CHANGE_SET' }
  | { type: 'PRICING_TYPE_UPDATED', value: PricingType, serverState: ServerState, previousValue: PricingType, workareaId: number }
  | { type: 'SYNC_RECURRING_WORKAREA_ITEMS', masterWorkareaId: number, childWorkareaIds: number[], removeNonMatchingItems: boolean, serverState: ServerState }
  | { type: 'COPY_WORKAREA_ITEMS', fromWorkareaId: number, toWorkareaId: number, serverState: ServerState }
  | { type: 'BULK_PRODUCTION_RATES_APPLIED', changes: QuantityChange[], serverState: ServerState }
  | { type: 'CLEAR_ERROR_MESSAGE' }


export type ChangeSet = {
  updatedItems: WorkareaItemBase[]
  addedItems: WorkareaItemBase[]
  deletedItems: string[]
  catalogItemsForAddedItems: CatalogItem[]
  templateItemsForAddedItems: TemplateItem[]
  errorMessage?: string
}

export type Dispatch = (action: Action) => void

export type ServerState = {
  workareaItems: WorkareaItemBase[]
  catalogItems: CatalogItem[]
  templateItems: TemplateItem[]
  estimatedQuantities?: Record<string, number>
}

export const createDefaultState = (): ChangeSet => ({
  updatedItems: [],
  addedItems: [],
  deletedItems: [],
  catalogItemsForAddedItems: [],
  templateItemsForAddedItems: [],
})

const updateMatch = (array: WorkareaItemBase[], guid: string, updated: WorkareaItemBase): WorkareaItemBase[] => {
  return array.map(x => x.guid === guid ? updated : x)
}

const findItemByGuid = (state: ChangeSet, serverState: ServerState, guid: string): WorkareaItemBase | undefined => {
  return state.addedItems.find(x => x.guid === guid) ?? state.updatedItems.find(x => x.guid === guid) ?? serverState.workareaItems.find(x => x.guid === guid)
}

export const updateByGuid = (state: ChangeSet, serverState: ServerState, guid: string, update: (x: WorkareaItemBase) => WorkareaItemBase): ChangeSet => {
  const addedItem = state.addedItems.find(x => x.guid === guid)
  if (addedItem) {
    const newUpdate = update(addedItem)
    return {
      ...state,
      addedItems: updateMatch(state.addedItems, guid, newUpdate)
    }
  }

  const updatedItem = state.updatedItems.find(x => x.guid === guid)
  if (updatedItem) {
    const newUpdate = update(updatedItem)
    return {
      ...state,
      updatedItems: updateMatch(state.updatedItems, guid, newUpdate)
    }
  }

  const serverItem = serverState.workareaItems.find(x => x.guid === guid)
  if (serverItem) {
    const newUpdate = update(serverItem)
    return {
      ...state,
      updatedItems: [...state.updatedItems, newUpdate]
    }
  }

  return state
}

export const reducer = (state: ChangeSet, action: Action): ChangeSet => {
  try {
    switch (action.type) {
      case 'NAME_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateName(x, action.value))
      case 'IS_ADDITIONAL_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateIsAdditional(x as WorkareaItem.ActualItem, getWorkarea(action.workareas, x.workareaId).pricingType, action.isVisitReviewed, action.value))
      case 'BILLABLE_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateIsBillable(x, getWorkarea(action.workareas, x.workareaId).pricingType, action.isVisitReviewed, action.value))
      case 'UNSURE_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateIsBillableUnknown(x as WorkareaItem.ActualItem, getWorkarea(action.workareas, x.workareaId).pricingType, action.isVisitReviewed, action.value))
      case 'TAXABLE_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateChargeTaxOnInvoice(x, action.isVisitReviewed, action.value))
      case 'QUANTITY_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateQuantity(x, action.value))
      case 'CUSTOM_COST_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateCustomCost(x, action.value))
      case 'CUSTOM_PRICE_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateCustomUnitPrice(x, action.value))
      case 'CUSTOM_TOTAL_PRICE_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateCustomTotalPrice(x as WorkareaItem.ActualItem, action.value))
      case 'CUSTOM_MARGIN_CHANGED':
        return updateByGuid(state, action.serverState, action.guid, x => WorkareaItem.updateCustomProfitMargin(x, action.value))
      case 'UPDATE_SORTING':
        return updateSorting(state, action.serverState, action.fromGuid, action.toGuid)
      case 'ADD_ITEM_FROM_CATALOG':
        return addItemFromCatalog(state, action.catalogItem, action.addItemParams)
      case 'ADD_TEMPLATE_FROM_CATALOG':
        return addTemplateFromCatalog(state, action.template, action.addItemParams)
      case 'DELETE_ITEM':
        return deleteItem(state, action.serverState, action.guid)
      case 'UNDELETE_ITEM':
        return undeleteItem(state, action.guid)
      case 'RECEIPT_DELETED':
        return deleteReceiptItems(state, action.serverState, action.receiptId)
      case 'RECEIPT_UNDELETED':
        return undeleteReceiptItems(state, action.serverState, action.receiptId)
      case 'SYNC_ITEM_PRICING':
        return syncItemPricing(state, action.serverState, action.budgets, action.guid)
      case 'SYNC_WORKAREA_PRICING':
        return syncWorkareaPricing(state, action.serverState, action.budgets, action.workareaId)
      case 'CLEAR_CHANGE_SET':
        return createDefaultState()
      case 'PRICING_TYPE_UPDATED':
        return updateBillableOnPricingTypeChange(state, action.serverState, action.workareaId, action.previousValue, action.value)
      case 'SYNC_RECURRING_WORKAREA_ITEMS':
        return syncRecurringWorkareaItems(state, action.masterWorkareaId, action.childWorkareaIds, action.removeNonMatchingItems, action.serverState)
      case 'COPY_WORKAREA_ITEMS':
        return copyWorkareaItems(action.fromWorkareaId, action.toWorkareaId, action.serverState, state)
      case 'BULK_PRODUCTION_RATES_APPLIED':
        return applyQuantityChanges(state, action.serverState, action.changes)
      case 'CLEAR_ERROR_MESSAGE':
        return { ...state, errorMessage: undefined }
      default: return state
    }
  } catch (error) {
    const errorMessage = (error as Error)?.message === 'Missing budget'
      ? 'The item could not be added because the budget for this job is missing.'
      : 'An error occurred.'
    return { ...state, errorMessage }
  }
}

const getWorkarea = (workareas: ItemWorkareaData[], workareaId: number): ItemWorkareaData => {
  const workarea = workareas.find(x => x.id === workareaId)
  if (!workarea) {
    throw new Error(`Unable to find workarea with id: ${workareaId}`)
  }
  return workarea
}

const getWorkareaFallibly = (workareas: ItemWorkareaData[], workareaId: number): ItemWorkareaData | undefined => {
  return workareas.find(x => x.id === workareaId)
}

export const toViewItem = (
  item: WorkareaItemBase,
  catalogItems: CatalogItem[],
  templateItems: TemplateItem[],
  visitData: ItemVisitData[],
  isDeleted: boolean,
  budgets: YearlyBudget[],
  projectIsSold: boolean,
  workareas: ItemWorkareaData[],
  estimatedQuantities?: Record<string, number>
): ViewItem => {
  const estimated = item as WorkareaItem.EstimatedItem
  const actual = item as WorkareaItem.ActualItem

  const catalogItem = catalogItems.find(x => x.guid === item.catalogItemGuid)
  const templateItem = templateItems.find(x => x.id === estimated.templateItemId)
  const visit = visitData.find(x => x.id === actual.visitId)
  const isVisitReviewed = visit?.billed ?? false
  const workarea = getWorkareaFallibly(workareas, item.workareaId)

  return {
    id: item.id,
    guid: item.guid,
    name: item.name,
    defaultName: item.catalogName,
    hasCustomName: item.name !== item.catalogName && actual.isFromTimesheets !== true,
    canCustomizeName: actual.isFromTimesheets !== true,
    units: item.units,
    cost: item.cost,
    totalCost: item.cost * item.quantity,
    hasCustomCost: hasValue(item.customCost),
    canCustomizeCost: actual.isFromTimesheets !== true,
    defaultCost: item.catalogCost,
    costPlusTax: item.cost + item.tax,
    taxPercentage: item.taxPercentage,
    applyTaxToBreakeven: item.applyTaxToBreakeven,
    overheadPercentage: item.overheadPercentage,
    breakeven: item.breakeven,
    profitMargin: item.profitMargin,
    hasCustomProfitMargin: hasValue(item.customProfitMargin),
    canCustomizeProfitMargin: actual.isFromTimesheets !== true,
    defaultProfitMargin: item.catalogCustomProfitMargin ?? item.originalProfitMargin,
    totalBreakeven: item.totalBreakeven,
    unitPrice: item.unitPrice,
    hasCustomUnitPrice: hasValue(item.customUnitPrice),
    originalCatalogCustomPrice: item.originalCatalogCustomPrice,
    defaultUnitPrice: WorkareaItem.updateCustomUnitPrice(item, null).unitPrice,
    totalPrice: item.totalPrice,
    defaultTotalPrice: WorkareaItem.isActualItem(item) ? WorkareaItem.updateCustomTotalPrice(item, null).totalPrice : item.totalPrice,
    canCustomizeTotalPrice: actual.isFromTimesheets === true,
    hasCustomTotalPrice: hasValue(actual.customTotalPrice),
    quantity: item.quantity,
    isBillable: item.isBillable,
    isBillableLocked: workarea ? WorkareaItem.isBillableLocked(item, workarea.pricingType, isVisitReviewed) : true,
    sortIndex: item.sortIndex,
    isInvoiced: hasValue(actual.invoiceId),
    isEntireRowDisabled: hasValue(actual.invoiceId) || isVisitReviewed || isDeleted,
    rateCalculationData: getRateCalculationData(catalogItem, templateItem),
    isAdditional: actual.isAdditional === true,
    isAdditionalLocked: WorkareaItem.isAdditionalLocked(actual, isVisitReviewed),
    isVisitReviewed,
    isDeleted,
    catalogItemId: item.catalogItemId,
    shouldShowPricingSync: shouldShowPricingSync(item, catalogItem, budgets, projectIsSold),
    chargeTaxOnInvoice: item.chargeTaxOnInvoice,
    unsure: actual.isBillableUnknown === true,
    workareaName: workarea?.name ?? '',
    totalEstimatedQuantity: estimatedQuantities?.[groupKeyForReceipts(item)],
    catalogItemGuid: item.catalogItemGuid,
    workareaId: item.workareaId,
    isFromTimesheets: actual.isFromTimesheets === true,
    visit,
    origin: actual.origin,
    originOwner: actual.originOwner,
    class: item.class,
    dragId: item.guid,
    ifBillableTotalPrice: item.isBillable ? undefined : WorkareaItem.recalculateWorkareaItemPricing({ ...item, isBillable: true }).totalPrice,
    webEntryConsumptionDate: actual.webEntryConsumptionDate,
  }
}

export const shouldShowPricingSync = (workareaItem: WorkareaItemBase, catalogItem: CatalogItem | undefined, budgets: YearlyBudget[], projectIsSold: boolean): boolean => {
  if (!catalogItem) {
    return false
  }

  return !(projectIsSold || catalogItem.isHidden || WorkareaItem.checkPricingSync(workareaItem, catalogItem, budgets))
}

export const getRateCalculationData = (catalogItem?: CatalogItem, templateItem?: TemplateItem): RateCalculationData | undefined => {
  const useTemplateItem = hasValue(templateItem?.rateInstallationQuantity) &&
    hasValue(templateItem?.ratePerUnits) &&
    hasValue(templateItem?.rateUsageQuantity)

  const name = useTemplateItem ? templateItem?.itemName ?? catalogItem?.name : catalogItem?.name
  const rateInstallationQuantity = useTemplateItem ? templateItem?.rateInstallationQuantity : catalogItem?.rateInstallationQuantity
  const ratePerUnits = useTemplateItem ? templateItem?.ratePerUnits : catalogItem?.ratePerUnits
  const rateUsageQuantity = useTemplateItem ? templateItem?.rateUsageQuantity : catalogItem?.rateUsageQuantity

  if (!hasValue(rateInstallationQuantity) || !hasValue(ratePerUnits) || !hasValue(rateUsageQuantity)) {
    return undefined
  }

  return {
    name: name ?? '',
    rateInstallationQuantity: rateInstallationQuantity!,
    ratePerUnits: ratePerUnits!,
    rateUsageQuantity: rateUsageQuantity!,
    units: catalogItem?.units ?? ''
  }
}

export type TemplateRateTarget = {
  guid: string
  name: string
  units: string
  quantity: number
  templateId?: number
  ratePerUnits: string
  rateInstallationQuantity: number
  rateUsageQuantity: number
}

export const getTemplateRateTargets = (state: ChangeSet, serverState: ServerState, workareaId: number): TemplateRateTarget[] => {
  const templateItems = [...serverState.templateItems, ...state.templateItemsForAddedItems]

  return serverState.workareaItems.concat(state.addedItems)
    .map(x => getUpdatedItem(x, state))
    .filter(x => !x.isDeleted)
    .map(x => x.item)
    .filter(x => WorkareaItem.isEstimatedItem(x) && x.workareaId === workareaId)
    .map(item => {
      const templateItemId = (item as WorkareaItem.EstimatedItem).templateItemId
      const templateItem = hasValue(templateItemId)
        ? templateItems.find(x => x.id === templateItemId)
        : undefined

      if (!hasValue(templateItem)
        || !hasValue(templateItem.rateInstallationQuantity)
        || !hasValue(templateItem.ratePerUnits)
        || !hasValue(templateItem.rateUsageQuantity)
        || normalizeRateUnits(templateItem.ratePerUnits) === '') {
        return undefined
      }

      return {
        guid: item.guid,
        name: item.name,
        units: item.units,
        quantity: item.quantity,
        templateId: templateItem.templateId,
        ratePerUnits: templateItem.ratePerUnits,
        rateInstallationQuantity: templateItem.rateInstallationQuantity,
        rateUsageQuantity: templateItem.rateUsageQuantity,
      }
    })
    .filter(hasValue)
}

const groupTargetsByTemplate = <T>(targets: TemplateRateTarget[], toItems: (templateId: number | undefined, targets: TemplateRateTarget[]) => T[]): TemplateGroup<T>[] => {
  return targets
    .map(x => x.templateId)
    .filter((templateId, index, all) => all.indexOf(templateId) === index)
    .sort((a, b) => {
      if (!hasValue(a) || !hasValue(b)) {
        return hasValue(a) ? -1 : 1
      }
      return a - b
    })
    .map(templateId => ({
      templateId,
      items: toItems(templateId, targets.filter(x => x.templateId === templateId))
    }))
    .filter(x => x.items.length > 0)
}

export const getTemplateRateGroups = (state: ChangeSet, serverState: ServerState, workareaId: number): TemplateGroup<DistinctRateUnit>[] => {
  const targets = getTemplateRateTargets(state, serverState, workareaId)
  return groupTargetsByTemplate(targets, (templateId, targetsForTemplate) =>
    distinctRateUnits(templateId, targetsForTemplate.map(x => x.ratePerUnits)))
}

export const getTemplateRateChanges = (targets: TemplateRateTarget[], entered: EnteredRateQuantities): QuantityChange[] => {
  return targets
    .map(target => {
      const installationQuantity = entered[rateEntryKey(target.templateId, target.ratePerUnits)]
      if (!hasValue(installationQuantity)) {
        return undefined
      }

      const newQuantity = calculateUsageQuantity(installationQuantity, target.rateInstallationQuantity, target.rateUsageQuantity)
      if (newQuantity === target.quantity) {
        return undefined
      }

      return {
        guid: target.guid,
        name: target.name,
        units: target.units,
        currentQuantity: target.quantity,
        newQuantity,
      }
    })
    .filter(hasValue)
}

export const getTemplateRateChangeGroups = (state: ChangeSet, serverState: ServerState, workareaId: number, entered: EnteredRateQuantities): TemplateGroup<QuantityChange>[] => {
  const targets = getTemplateRateTargets(state, serverState, workareaId)
  return groupTargetsByTemplate(targets, (_, targetsForTemplate) => getTemplateRateChanges(targetsForTemplate, entered))
}

export const applyQuantityChanges = (state: ChangeSet, serverState: ServerState, changes: QuantityChange[]): ChangeSet => {
  return changes.reduce((newState, change) =>
    updateByGuid(newState, serverState, change.guid, x => WorkareaItem.updateQuantity(x, change.newQuantity)), state)
}

export type ViewItemFilter = {
  workareaIds?: number[]
  visitIds?: string[]
  workareaItemType?: WorkareaItemType
  receiptId?: number
}

const filterAddedItem = (item: WorkareaItemBase, filter: ViewItemFilter) => {
  if (filter.workareaIds !== undefined && !filter.workareaIds.includes(item.workareaId)) {
    return false
  }

  const actual = item as WorkareaItem.ActualItem
  if (filter.visitIds !== undefined && !filter.visitIds.includes(actual.visitId ?? '')) {
    return false
  }

  if (filter.workareaItemType === WorkareaItemTypes.Actual && !WorkareaItem.isActualItem(item)) {
    return false
  }

  if (filter.workareaItemType === WorkareaItemTypes.Estimated && WorkareaItem.isActualItem(item)) {
    return false
  }

  if (filter.receiptId !== undefined && actual.receiptId !== filter.receiptId) {
    return false
  }

  return true
}

type UpdatedItemData = {
  item: WorkareaItem.WorkareaItemBase
  isDeleted: boolean
}

export const getUpdatedItem = (item: WorkareaItem.WorkareaItemBase, changeSet: ChangeSet): UpdatedItemData => {
  return {
    item: changeSet.updatedItems.find(x => x.guid === item.guid) ?? item,
    isDeleted: !!changeSet.deletedItems.find(x => x === item.guid)
  }
}

export const createViewItems = (
  state: ChangeSet,
  serverState: ServerState,
  visitData: ItemVisitData[],
  budgets: YearlyBudget[],
  projectIsSold: boolean,
  workareas: ItemWorkareaData[],
  filter: ViewItemFilter
): ViewItem[] => {
  const filteredAddedItems = state.addedItems.filter(x => filterAddedItem(x, filter))
  const catalogItems = [...serverState.catalogItems, ...state.catalogItemsForAddedItems]
  const templateItems = [...serverState.templateItems, ...state.templateItemsForAddedItems]

  const viewItems = serverState.workareaItems.concat(filteredAddedItems).map(x => {
    const { item, isDeleted } = getUpdatedItem(x, state)
    return toViewItem(
      item,
      catalogItems,
      templateItems,
      visitData,
      isDeleted,
      budgets,
      projectIsSold,
      workareas,
      serverState.estimatedQuantities
    )
  })
  viewItems.sort((a, b) => a.sortIndex - b.sortIndex)
  if (viewItems.length < 1) {
    viewItems.push({ ...defaultViewItem(), isEmptyRow: true })
  }
  return viewItems
}

export const updateSorting = (state: ChangeSet, serverState: ServerState, fromGuid: string, toGuid: string): ChangeSet => {
  const originalSorting = serverState.workareaItems.concat(state.addedItems).map(item => {
    const updated = state.updatedItems.find(x => x.guid === item.guid)
    return { guid: item.guid, sortIndex: updated?.sortIndex ?? item.sortIndex }
  })
  originalSorting.sort((a, b) => a.sortIndex - b.sortIndex)

  const fromIndex = originalSorting.findIndex(x => x.guid === fromGuid)
  const toIndex = originalSorting.findIndex(x => x.guid === toGuid)

  return arrayMove(originalSorting, fromIndex, toIndex)
    .map((x, i) => ({ ...x, newSortIndex: i }))
    .filter(x => x.newSortIndex !== x.sortIndex)
    .reduce((newState, x) => updateByGuid(newState, serverState, x.guid, y => ({ ...y, sortIndex: x.newSortIndex })), state)
}

export type AddItemParams = {
  serverState: ServerState
  quantity: number
  workareaItemType: WorkareaItemType
  workareaId: number
  workareas: ItemWorkareaData[]
  budgets: YearlyBudget[]
  sortIndex: number
  toGuid?: string
  visitId?: string
  origin?: ActualItemOrigin
  receiptId?: number
  templateItemId?: number
  name?: string
}
export const addItemFromCatalog = (state: ChangeSet, catalogItem: CatalogItem, params: AddItemParams): ChangeSet => {
  const input = {
    workareaId: params.workareaId,
    catalogItem,
    budgets: params.budgets,
    pricingType: getWorkarea(params.workareas, params.workareaId).pricingType,
    quantity: params.quantity,
    sortIndex: params.sortIndex,
    templateItemId: params.templateItemId,
    visitId: params.visitId,
    origin: params.origin,
    receiptId: params.receiptId,
    name: params.name
  }
  const item = params.workareaItemType === WorkareaItemTypes.Estimated
    ? WorkareaItem.createEstimatedItemFromCatalogItem(input)
    : WorkareaItem.createActualItemFromCatalogItem(input)

  const updated = addItemToChangeSet(state, item, catalogItem)
  return hasValue(params.toGuid) ? updateSorting(updated, params.serverState, item.guid, params.toGuid!) : updated
}

const addItemToChangeSet = (changeSet: ChangeSet, item: WorkareaItemBase, catalogItem?: CatalogItem): ChangeSet => {
  const catalogItemsForAddedItems = catalogItem
    ? [...changeSet.catalogItemsForAddedItems.filter(x => x.guid !== catalogItem.guid), catalogItem]
    : changeSet.catalogItemsForAddedItems

  return {
    ...changeSet,
    addedItems: [...changeSet.addedItems, item],
    catalogItemsForAddedItems
  }
}

export const addTemplateFromCatalog = (state: ChangeSet, template: CatalogTemplateWithItems, params: AddItemParams): ChangeSet => {
  let newSortIndex = params.sortIndex
  const stateWithNewTemplateItems = {
    ...state,
    templateItemsForAddedItems: [
      ...state.templateItemsForAddedItems.filter(x => !template.templateItems.some(y => y.id === x.id)),
      ...template.templateItems
    ]
  }
  return template.templateItems.reduce((newState, templateItem) => addItemFromCatalog(newState, templateItem.catalogItem, {
    ...params,
    quantity: templateItem.qty,
    sortIndex: newSortIndex++,
    templateItemId: templateItem.id,
    name: templateItem.itemName,
  }), stateWithNewTemplateItems)
}

export const deleteItem = (state: ChangeSet, serverState: ServerState, guid: string): ChangeSet => {
  const updatedItem = serverState.workareaItems.concat(state.addedItems).find(x => x.guid === guid)
  if (!updatedItem) {
    return state
  }

  if (state.deletedItems.includes(guid)) {
    return state
  }

  return {
    ...state,
    deletedItems: [...state.deletedItems, guid]
  }
}

export const undeleteItem = (state: ChangeSet, guid: string): ChangeSet => {
  return {
    ...state,
    deletedItems: state.deletedItems.filter(x => x !== guid),
  }
}

export const groupKeyForReceipts = (item: WorkareaItem.WorkareaItemBase): string => `${item.catalogItemGuid}-${item.workareaId}-${item.name}`

const groupItemsForReceipts = (items: WorkareaItem.WorkareaItemBase[]): Record<string, WorkareaItem.WorkareaItemBase[]> => {
  return groupBy(items, groupKeyForReceipts)
}

export const getEstimatedQuantities = (estimatedItems: WorkareaItem.EstimatedItem[]): Record<string, number> => {
  const groupedItems = groupItemsForReceipts(estimatedItems)
  return Object.fromEntries(Object.entries(groupedItems).map(([key, value]) => {
    return [key, value.reduce((acc, x) => acc + x.quantity, 0)]
  }))
}

export const calculateTotalStandardPrice = (item: WorkareaItemBase | ViewItem): number => {
  const basePrice = WorkareaItem.calculateUnitPrice(item.breakeven, item.profitMargin)
  return basePrice * item.quantity
}

const defaultLaborBillableForPricingType = (pricingType: PricingType): boolean => {
  switch (pricingType) {
    case PricingTypes.QuotedPrice:
    case PricingTypes.TimeAndMaterials:
    case PricingTypes.PerVisit:
      return true
    case PricingTypes.FlatRate:
    case PricingTypes.Unbillable:
    default:
      return false
  }
}

const defaultNonLaborItemBillableForPricingType = (pricingType: PricingType, itemClass: ItemClass): boolean => {
  switch (pricingType) {
    case PricingTypes.Unbillable:
      return false
    case PricingTypes.FlatRate:
      return itemClass !== ItemClasses.Labor
    default:
      return true
  }
}

const shouldUpdateNonLaborItemBillable = (oldPricingType: PricingType, newPricingType: PricingType): boolean => {
  return [newPricingType, oldPricingType].some(x => [PricingTypes.QuotedPrice, PricingTypes.Unbillable].includes(x))
}

const syncItemPricing = (changeSet: ChangeSet, serverState: ServerState, budgets: YearlyBudget[], guid: string): ChangeSet => {
  const item = findItemByGuid(changeSet, serverState, guid)
  if (!item) {
    return changeSet
  }

  const catalogItem = serverState.catalogItems.find(y => y.guid === item.catalogItemGuid) ?? changeSet.catalogItemsForAddedItems.find(y => y.guid === item.catalogItemGuid)
  if (!catalogItem || WorkareaItem.checkPricingSync(item, catalogItem, budgets)) {
    return changeSet
  }

  return updateByGuid(changeSet, serverState, guid, x => WorkareaItem.syncPricing(x, catalogItem, budgets))
}

const syncWorkareaPricing = (changeSet: ChangeSet, serverState: ServerState, budgets: YearlyBudget[], workareaId: number): ChangeSet => {
  const allItemIds = serverState.workareaItems.concat(changeSet.addedItems).filter(x => x.workareaId === workareaId).map(x => x.guid)
  return allItemIds.reduce((updatedChangeSet, guid) => syncItemPricing(updatedChangeSet, serverState, budgets, guid), changeSet)
}

export const updateBillableOnPricingTypeChange = (changeSet: ChangeSet, serverState: ServerState, workareaId: number, previousPricingType: PricingType, newPricingType: PricingType) => {
  const allItemIds = serverState.workareaItems.concat(changeSet.addedItems).filter(x => x.workareaId === workareaId).map(x => x.guid)

  const defaultLaborBillable = defaultLaborBillableForPricingType(newPricingType)
  const shouldUpdateNonLaborItems = shouldUpdateNonLaborItemBillable(previousPricingType, newPricingType)
  return allItemIds.reduce((state, guid) => {
    return updateByGuid(state, serverState, guid, x => {
      if (x.class === ItemClasses.Labor) {
        return WorkareaItem.updateIsBillableForPricingTypeChange(x, defaultLaborBillable)
      }
      if (shouldUpdateNonLaborItems) {
        return WorkareaItem.updateIsBillableForPricingTypeChange(x, defaultNonLaborItemBillableForPricingType(newPricingType, x.class))
      }
      return x
    })
  }, changeSet)
}

type SyncRecurringWorkareaItemsContext = {
  changeSet: ChangeSet,
  serverState: ServerState,
  masterWorkareaItems: UpdatedItemData[]
  childWorkareaItems: UpdatedItemData[]
  childWorkareaId: number
}

export const syncRecurringWorkareaItems = (changeSet: ChangeSet, masterWorkareaId: number, childWorkareaIds: number[], removeNonMatchingItems: boolean, serverState: ServerState): ChangeSet => {
  const allItems = serverState.workareaItems.concat(changeSet.addedItems)
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => WorkareaItem.isEstimatedItem(x.item))

  const context: SyncRecurringWorkareaItemsContext = {
    changeSet,
    serverState,
    masterWorkareaItems: allItems.filter(x => x.item.workareaId === masterWorkareaId),
    childWorkareaItems: [],
    childWorkareaId: 0
  }

  childWorkareaIds.forEach(childWorkareaId => {
    context.childWorkareaId = childWorkareaId
    context.childWorkareaItems = allItems.filter(x => x.item.workareaId === childWorkareaId)

    updateChildWorkareaItems(context)

    if (removeNonMatchingItems) {
      removeNonMatchingChildWorkareaItems(context)
    }
  })

  return context.changeSet
}

const updateChildWorkareaItems = (context: SyncRecurringWorkareaItemsContext) => {
  context.masterWorkareaItems.forEach(master => {
    const foundMatch = updateMatchingChildWorkareaItems(context, master)

    if (!foundMatch) {
      addItemToChildWorkarea(context, master)
    }
  })
}

const updateMatchingChildWorkareaItems = (context: SyncRecurringWorkareaItemsContext, master: UpdatedItemData): boolean => {
  let foundMatch = false

  context.childWorkareaItems.forEach(child => {
    if (isRecurringMatch(master.item, child.item)) {
      foundMatch = true
      context.changeSet = updateByGuid(context.changeSet, context.serverState, child.item.guid, () => ({
        ...master.item,
        id: child.item.id,
        guid: child.item.guid,
        workareaId: child.item.workareaId
      }))

      if (master.isDeleted !== child.isDeleted) {
        if (master.isDeleted) {
          context.changeSet = deleteItem(context.changeSet, context.serverState, child.item.guid)
        } else {
          context.changeSet = undeleteItem(context.changeSet, child.item.guid)
        }

      }
    }
  })

  return foundMatch
}

const addItemToChildWorkarea = (context: SyncRecurringWorkareaItemsContext, master: UpdatedItemData) => {
  const newGuid = WorkareaItem.generateNewItemId()
  context.changeSet = addItemToChangeSet(context.changeSet, {
    ...master.item,
    id: 0,
    guid: newGuid,
    workareaId: context.childWorkareaId
  })

  if (master.isDeleted) {
    context.changeSet = deleteItem(context.changeSet, context.serverState, newGuid)
  }
}

const removeNonMatchingChildWorkareaItems = (context: SyncRecurringWorkareaItemsContext) => {
  const nonMatching = context.childWorkareaItems.filter(child => !context.masterWorkareaItems.some(master => isRecurringMatch(master.item, child.item)))
  nonMatching.forEach(nonMatch => {
    context.changeSet = deleteItem(context.changeSet, context.serverState, nonMatch.item.guid)
  })
}

export const copyWorkareaItems = (fromWorkareaId: number, toWorkareaId: number, serverState: ServerState, changeSet: ChangeSet): ChangeSet => {
  const items = serverState.workareaItems
    .concat(changeSet.addedItems)
    .filter(x => x.workareaId === fromWorkareaId && WorkareaItem.isEstimatedItem(x))
    .map(x => getUpdatedItem(x, changeSet))
  const copies = items.map(x => ({ item: WorkareaItem.copyToWorkarea(x.item, toWorkareaId), isDeleted: x.isDeleted }))
  return {
    ...changeSet,
    addedItems: [
      ...changeSet.addedItems,
      ...copies.map(x => x.item)
    ],
    deletedItems: [
      ...changeSet.deletedItems,
      ...copies.filter(x => x.isDeleted).map(x => x.item.guid)
    ]
  }
}

const getReceiptItems = (changeSet: ChangeSet, serverState: ServerState, receiptId: number) => {
  return serverState.workareaItems.concat(changeSet.addedItems)
    .map(x => getUpdatedItem(x, changeSet))
    .filter(x => WorkareaItem.isActualItem(x.item) && x.item.receiptId === receiptId)
}

export const deleteReceiptItems = (changeSet: ChangeSet, serverState: ServerState, receiptId: number): ChangeSet => {
  return getReceiptItems(changeSet, serverState, receiptId)
    .reduce((updated, x) => deleteItem(updated, serverState, x.item.guid), changeSet)
}

export const undeleteReceiptItems = (changeSet: ChangeSet, serverState: ServerState, receiptId: number): ChangeSet => {
  return getReceiptItems(changeSet, serverState, receiptId)
    .reduce((updated, x) => undeleteItem(updated, x.item.guid), changeSet)
}

export const workareaIdsWithUnsyncedItems = (changeSet: ChangeSet, serverState: ServerState, budgets: YearlyBudget[], projectIsSold: boolean): number[] => {
  if (projectIsSold) {
    return []
  }

  const workareaIds = new Set<number>()
  const allCatalogItems = [...serverState.catalogItems, ...changeSet.catalogItemsForAddedItems]

  serverState.workareaItems.concat(changeSet.addedItems).forEach(item => {
    if (workareaIds.has(item.workareaId)) {
      return
    }

    const catalogItem = allCatalogItems.find(x => x.guid === item.catalogItemGuid)
    const updated = getUpdatedItem(item, changeSet)
    if (shouldShowPricingSync(updated.item, catalogItem, budgets, projectIsSold)) {
      workareaIds.add(updated.item.workareaId)
    }
  })
  return Array.from(workareaIds)
}

export type UpdateJobItemsMutationData = {
  jobId: number
  serverRequest: UpdateJobItemsRequest,
  optimisticUpdate: GetJobWorkareaItemsResponse
}

export const buildUpdateJobItemsMutationData = (jobId: number, changeSet: ChangeSet, serverData: GetJobWorkareaItemsResponse): UpdateJobItemsMutationData => {
  const addedEstimatedItems = changeSet.addedItems
    .filter(WorkareaItem.isEstimatedItem)
    .filter(x => !changeSet.deletedItems.includes(x.guid))
  const addedActualItems = changeSet.addedItems
    .filter(WorkareaItem.isActualItem)
    .filter(x => !changeSet.deletedItems.includes(x.guid))
  const updatedEstimatedItems = changeSet.updatedItems
    .filter(WorkareaItem.isEstimatedItem)
    .filter(x => !changeSet.deletedItems.includes(x.guid))
  const updatedActualItems = changeSet.updatedItems
    .filter(WorkareaItem.isActualItem)
    .filter(x => !changeSet.deletedItems.includes(x.guid))

  const catalogItemsToAppend = changeSet.catalogItemsForAddedItems
    .filter(x => !serverData.catalogItems.some(y => y.guid === x.guid))
  const templateItemsToAppend = changeSet.templateItemsForAddedItems
    .filter(x => !serverData.templateItems.some(y => y.id === x.id))

  return {
    jobId,
    serverRequest: {
      addedEstimatedItems,
      addedActualItems,
      updatedEstimatedItems,
      updatedActualItems,
      deletedItems: [...changeSet.deletedItems]
    },
    optimisticUpdate: {
      ...serverData,
      estimatedItems: serverData.estimatedItems.concat(addedEstimatedItems)
        .map(x => getUpdatedItem(x, changeSet))
        .filter(x => !x.isDeleted)
        .map(x => x.item as WorkareaItem.EstimatedItem),
      actualItems: serverData.actualItems.concat(addedActualItems)
        .map(x => getUpdatedItem(x, changeSet))
        .filter(x => !x.isDeleted)
        .map(x => x.item as WorkareaItem.ActualItem),
      catalogItems: [
        ...serverData.catalogItems,
        ...catalogItemsToAppend
      ],
      templateItems: [
        ...serverData.templateItems,
        ...templateItemsToAppend
      ]
    }
  }
}
